import { useEffect, useState } from "react";
import axios from 'axios';
import styled from "styled-components";
import fundo from "../../../../styles/fundo.svg";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaDownload } from "react-icons/fa";
import { FiZoomIn, FiZoomOut } from "react-icons/fi";


import logo1 from "./logo-1.png";
import logo6 from "./logo-6.png";


// ========== LOOK & FEEL MINIMALISTA ==========
const palette = {
  // fundo da página (se usar)
  cardBg: "#ffffff",
  headBg: "#0a7a3f",          // cabeçalho da tabela
  headText: "#ffffff",
  text: "#334155",
  textMuted: "#64748b",
  border: "#e5e7eb",
  zebra: "#f8fafc",

  accent: "#0ea5e9",
};

// Container do card/tabela
const Geral = styled.div`
  overflow: hidden;
  background: ${palette.cardBg};
  border: 1px solid ${palette.border};
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  margin: 10px auto;
  width: 100%;

  /* Envolve a tabela num scroller vertical suave */
  & > div {
    overflow: auto;
  }

  /* Scrollbar minimal */
  & > div::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }
  & > div::-webkit-scrollbar-track {
    background: transparent;
  }
  & > div::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 999px;
  }
  & > div::-webkit-scrollbar-thumb:hover {
    background: #c4c9d1;
  }

  @media (max-width: 1367px) {
    margin: 8px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  }

  @media (max-width: 1024px) {
    margin: 8px;
    margin-bottom: 100px;
  }
`;

// Tabela
const Table = styled.table`
  border-collapse: collapse;   /* 👈 junto com as bordas fica linear */
  width: 100%;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto,
    "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif;


  /* borda inferior de cada linha do corpo */
  tbody tr:not(:last-child) td {
    border-bottom: 1px solid ${palette.border};
  }
`;

// Cabeçalho sticky com fallback de cor
const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${palette.headBg};
  background-blend-mode: multiply;
  backdrop-filter: saturate(120%) blur(2px);
`;

// Linhas
export const Tr = styled.tr`
  /* ❌ remova esta linha:
  border-bottom: 1px solid ${palette.border};
  */

  ${({ noHover }) =>
    !noHover &&
    `
    &:hover {
      background-color: ${palette.zebra}; /* ou outra cor de hover */
    }
  `}

  @media (max-width: 1024px) {
    .hide-on-mobile {
      display: none;
    }
  }
`;

// Cabeçalho das colunas
export const Th = styled.th`
  color: ${palette.headText};
  padding: clamp(8px, 1.6vw, 14px) clamp(6px, 1.4vw, 10px);
  font-size: clamp(8px, 1.2vw, 11px);
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  white-space: nowrap;

  &.hide-on-mobile {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  .tooltip {
    position: relative;
    cursor: help;
  }
  .tooltip .tooltiptext {
    visibility: hidden;
    width: max-content;
    background-color: #111827;
    color: #fff;
    text-align: left;
    border-radius: 8px;
    padding: 4px 8px;
    position: absolute;
    z-index: 10;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity .2s ease;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(0,0,0,.2);
  }
  .tooltip:hover .tooltiptext {
    visibility: visible;
    opacity: 1;
  }
`;

// Células
export const Td = styled.td`
  padding: clamp(10px, 1.8vw, 16px) clamp(6px, 1.4vw, 10px);
  text-align: ${(props) => (props.alignCenter ? 'center' : 'left')};
  font-size: clamp(8px, 1.4vw, 11px);
  color: ${palette.text};
  background-color: ${(props) => (props.zebra ? palette.zebra : "transparent")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.mobile-hide {
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

// Corpo
export const Tbody = styled.tbody`
  background-color: #fff;
`;

// TH que funciona como “cabeçalho-título” (linha 1)
export const CabecalhoTabela = styled.th`
  text-align: center;
  padding: clamp(10px, 2vw, 18px);
  color: ${palette.headText};
  background-color: ${palette.headBg};
  position: sticky;
  top: 0;
  z-index: 6; /* acima do Thead */
  border-bottom: 1px solid rgba(255,255,255,0.08);

  h2 {
    margin: 0;
    font-size: clamp(14px, 2.4vw, 18px);
    font-weight: 700;
    letter-spacing: 0.01em;
  }
`;

// Botão “fantasma” no header (usado para ícones)
const Button = styled.td`
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: transparent;
  color: ${palette.headText};
  opacity: .9;
  transition: transform .12s ease, opacity .12s ease;
  user-select: none;

  &:hover { transform: translateY(-1px); opacity: 1; }
  &:active { transform: translateY(0); opacity: .85; }

  @media (max-width: 780px) {
    font-size: 14px;
  }
`;

// Header do título/ações
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 780px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;

    h2 { font-size: clamp(16px, 5vw, 20px); }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  p {
    margin: 0;
    font-size: 12px;
    color: ${palette.headText};
    opacity: .85;
  }

  @media (max-width: 780px) {
    width: 100%;
    justify-content: space-between;
    p { font-size: 11px; }
  }
`;

const IconGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  /* alvo o <td> Button que contém ícones */
  ${Button} {
    padding: 6px 8px;
    border-radius: 10px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
  }
`;



const useBoletim = (tipo, urlApi, rota = "atualiza-milho", periodo = null) => {
  const [dados, setDados] = useState([]);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        const res = await axios.get(`${urlApi}boletins-externo/${rota}/${tipo}`);

        const ordem = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'ano'];

        // 🔒 Verifica se é array antes de tentar ordenar
        if (Array.isArray(res.data)) {
          const dadosOrdenados = res.data.sort((a, b) => {
            return ordem.indexOf(a.PERIODO) - ordem.indexOf(b.PERIODO);
          });
          setDados(dadosOrdenados);
        } else {
          console.warn(`⚠️ Dados inesperados recebidos para tipo ${tipo}:`, res.data);
          setDados([]); // ou setDados([res.data]) se for um único objeto válido
        }

      } catch (error) {
        console.error(`Erro ao buscar ${tipo}:`, error);
      }
    };

    fetchDados();
  }, [tipo, urlApi, rota, periodo]);

  return dados;
};

const useHoraRodou = (urlApi, rota = "atualiza-milho") => {
  const [hora, setHora] = useState(null);

  useEffect(() => {
    const fetchHora = async () => {
      try {
        const res = await axios.get(`${urlApi}boletins-externo/${rota}/hora-rodou`);
        setHora(res.data.hora); // já pega a string diretamente
      } catch (error) {
        console.error("Erro ao buscar hora-rodou:", error);
      }
    };

    fetchHora();
  }, [urlApi, rota]);

  return hora;
};




const Grid = ({ urlApi, zoomIn, zoomOut }) => {

  //Retorna os periodos para o cabeçalho

  const [periodos, setPeriodos] = useState("")

  useEffect(() => {
    const fetchPeriodos = async () => {
      const res = await axios.get(`${urlApi}boletins-externo/milho-periodos`);
      setPeriodos(res.data);
    };

    fetchPeriodos();
  }, []);

  const horaRodou = useHoraRodou(urlApi);


  const milhoRecebido = useBoletim("bmilho-recebido", urlApi);
  const milhoMoido = useBoletim("bmilho-moido", urlApi);
  const estoqueMilho = useBoletim("bmilho-estoque-milho", urlApi);
  const diasSafraMilho = useBoletim("bmilho-dias-safra-milho", urlApi);
  const horasSafraMilho = useBoletim("bmilho-horas-safra-milho", urlApi);
  const diasEfetivosMoagem = useBoletim("bmilho-dias-efetivos-moagem", urlApi);
  const horasEfetivasMoagem = useBoletim("bmilho-horas-efetivas-moagem", urlApi);
  const horasParadasTotais = useBoletim("bmilho-horas-paradas-totais", urlApi);
  const horasParadasNaoMoinho = useBoletim("bmilho-horas-paradas-nao-moinho", urlApi);
  const horasParadasMoinho = useBoletim("bmilho-horas-paradas-moinho", urlApi);
  const eficienciaIndustrial = useBoletim("bmilho-eficiencia-industrial-milho", urlApi);
  const eficienciaGeral = useBoletim("bmilho-eficiencia-geral", urlApi);
  const moagemHorasEfetivas = useBoletim("bmilho-moagem-horas-efetivas", urlApi);
  const etanolHidratadoProduzido = useBoletim("bmilho-etanol-hidratado-produzido", urlApi);
  const etanolProcesso = useBoletim("bmilho-etanol-em-processo", urlApi);
  const inpmEtanolHidratado = useBoletim("bmilho-inpm-etanol-hidratado", urlApi);
  const saidaEtanolHidratado = useBoletim("bmilho-saida-etanol-hidratado", urlApi);
  const estoqueEtanolHidratado = useBoletim("bmilho-estoque-etanol-hidratado", urlApi);
  const vinhacaProduzida = useBoletim("bmilho-vinhaca-produzida", urlApi);
  const etanolAnidroProduzido = useBoletim("bmilho-etanol-anidro-produzido", urlApi);
  const inpmEtanolAnidro = useBoletim("bmilho-inpm-etanol-anidro", urlApi);
  const saidaEtanolAnidro = useBoletim("bmilho-saida-etanol-anidro", urlApi);
  const estoqueEtanolAnidro = useBoletim("bmilho-estoque-etanol-anidro", urlApi);
  const wdgProduzido = useBoletim("bmilho-wdg-produzido", urlApi);
  const wdgMilhoMoido = useBoletim("bmilho-wdg-milho-moido", urlApi);
  const estoqueWdg = useBoletim("bmilho-estoque-wdg", urlApi);
  const ddgProduzido = useBoletim("bmilho-ddg-produzido", urlApi);
  const ddgMilhoMoido = useBoletim("bmilho-ddg-milho-moido", urlApi);
  const estoqueDdg = useBoletim("bmilho-estoque-ddg", urlApi);
  const etanolTotalHidratado = useBoletim("bmilho-etanol-total-hidratado", urlApi);
  const kgsDdgEtanolTotal = useBoletim("bmilho-hgs-ddg-etanol-total", urlApi);
  const tonDdgEtanolHidratado = useBoletim("bmilho-ton-ddg-etanol-hidratado", urlApi);
  const tonDdgEtanolAnidro = useBoletim("bmilho-ton-ddg-etanol-anidro", urlApi);
  const kgsEdgEtanolTotal = useBoletim("bmilho-ton-wdg-etanol-total", urlApi);
  const tonEdgEtanolHidratado = useBoletim("bmilho-ton-wdg-etanol-hidratado", urlApi);
  const tonEdgEtanolAnidro = useBoletim("bmilho-ton-wdg-etanol-anidro", urlApi);
  const milhoMoidoEtanolHidratado = useBoletim("bmilho-moido-etanol-hidratado", urlApi);
  const milhoMoidoEtanolAnidro = useBoletim("bmilho-moido-etanol-anidro", urlApi);
  const milhoMoidoEtanolHidratadoPorcentagem = useBoletim("bmilho-moido-etanol-hidratado-porcentagem", urlApi);
  const milhoMoidoEtanolAnidroPorcentagem = useBoletim("bmilho-moido-etanol-anidro-porcentagem", urlApi);
  const litrosEtanolMilhoMoido = useBoletim("bmilho-litro-etanol-milho-moido", urlApi);
  const litrosEtanolAmidoBaseSeca = useBoletim("bmilho-litro-etanol-amido-base-seca", urlApi);
  const rendimentoTotalWggLdgDdg = useBoletim("bmilho-rendimento-total-wgg-ldg-ddg", urlApi);
  const amidoBaseUmida = useBoletim("bmilho-amido-base-umida", urlApi);
  const umidade = useBoletim("bmilho-umidade", urlApi);
  const amidoBaseSeca = useBoletim("bmilho-amido-base-seca", urlApi);
  const impurezas = useBoletim("bmilho-impurezas", urlApi);
  const quebrados = useBoletim("bmilho-quebrados", urlApi);
  const avariados = useBoletim("bmilho-avariados", urlApi);

  //=======================JUNTA OS MAP======================================//
  const isMobile = window.innerWidth <= 768;
  const codigosPeriodos = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'ano'];
  const periodosMobile = ['d1', 'd', 'semana', 'mes', 'ano'];

  const LinhaTabela = ({ titulo, dados2 }) => {
    const periodosEmNegrito = ['d', 'semana', 'mes', 'ano'];

    const dadosFiltrados = isMobile
      ? dados2.filter(item => periodosMobile.includes(item.PERIODO))
      : dados2;

    return (
      <Tr>
        <Td><strong>{titulo}</strong></Td>
        {dadosFiltrados.map((item, index) => {
          const valorFormatado = item.VALOR != null && item.VALOR !== 0
            ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : "0";

          const estiloFonte = periodosEmNegrito.includes(item.PERIODO)
            ? { fontWeight: 'bold' }
            : {};

          return (
            <Td key={`2-${index}`} alignCenter zebra={index % 2 === 0} style={estiloFonte}>
              {valorFormatado}
            </Td>
          );
        })}
      </Tr>
    );
  };



  //=======================GERAR PDF==========================================//

  const getBase64FromImage = (imgSrc) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve({ base64: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight });
      };

      img.onerror = () => reject(new Error("Erro ao carregar imagem"));
      img.src = imgSrc;
    });
  };






  //===========================EXPORTAR PDF==============================//
  const gerarPdfRelatorio = async () => {
    const isMobile = window.innerWidth <= 768;
    const periodosOrdenados = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'ano'];
    const periodosMobile = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'ano'];
    const periodosFiltrados = isMobile ? periodosMobile : periodosOrdenados;

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });

    // Marca d'água (logo1 sempre)
    const { base64: marcaDaguaBase64, width: mw, height: mh } = await getBase64FromImage(logo1);
    const maxMarcaW = 400;
    const maxMarcaH = 300;
    let marcaW = mw, marcaH = mh;
    if (mw > maxMarcaW || mh > maxMarcaH) {
      const ratio = Math.min(maxMarcaW / mw, maxMarcaH / mh);
      marcaW = mw * ratio;
      marcaH = mh * ratio;
    }
    const centerX = (842 - marcaW) / 2;
    const centerY = (595 - marcaH) / 2;

    // Cabeçalho fixo
    const { base64, width, height } = await getBase64FromImage(logo6);
    let displayW = width, displayH = height;
    const maxW = 200, maxH = 60;
    if (width > maxW || height > maxH) {
      const ratio = Math.min(maxW / width, maxH / height);
      displayW = width * ratio;
      displayH = height * ratio;
    }

    doc.addImage(base64, 'PNG', 20, 10, displayW, displayH);
    doc.setFontSize(10);
    doc.setTextColor(87, 87, 87);
    doc.text("Boletim Milho", 30, 70);
    doc.text(`Última atualização: ${horaRodou}`, 600, 70);

    // Mapeia os códigos d7, d6... para as datas reais
    const mapaCodigosParaData = {};
    periodosOrdenados.forEach((codigo, index) => {
      if (periodos[index]) {
        mapaCodigosParaData[codigo] = periodos[index].DATA;
      }
    });
    mapaCodigosParaData['semana'] = 'Semana';
    mapaCodigosParaData['mes'] = 'Mês';
    mapaCodigosParaData['ano'] = 'Ano';


    // Cabeçalhos
    const headers = [
      "Período",
      ...periodosFiltrados.map(p => mapaCodigosParaData[p] || p.toUpperCase())
    ];



    // Corpo
    const body = [];

    const montarLinha = (titulo, dados) => {
      const linha = [titulo];
      periodosFiltrados.forEach(periodo => {
        const item = dados.find(d => d.PERIODO === periodo);
        linha.push(item && item.VALOR != 0 ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "0");
      });
      body.push(linha);
    };

    // Linhas da tabela
    montarLinha("Milho Recebido (ton)", milhoRecebido);
    montarLinha("Milho Moído (ton)", milhoMoido);
    montarLinha("Estoque Milho (ton)", estoqueMilho);
    montarLinha("Dias de Safra (dia)", diasSafraMilho);
    montarLinha("Horas de Safra (h)", horasSafraMilho);
    montarLinha("Dias Efetivos de Moagem (dia)", diasEfetivosMoagem);
    montarLinha("Horas Efetivas de Moagem (h)", horasEfetivasMoagem);
    montarLinha("Horas Paradas Totais (h)", horasParadasTotais);
    montarLinha("Horas Paradas Não Moinho (h)", horasParadasNaoMoinho);
    montarLinha("Horas Paradas Moinho (h)", horasParadasMoinho);
    montarLinha("Eficiência Industrial (%)", eficienciaIndustrial);
    montarLinha("Eficiência Geral (%)", eficienciaGeral);
    montarLinha("Moagem Horas Efetivas (%)", moagemHorasEfetivas);
    montarLinha("Etanol Hidratado Produzido (l)", etanolHidratadoProduzido);
    montarLinha("Etanol em Processo (l)", etanolProcesso);
    montarLinha("INPM Etanol Hidratado (inpm)", etanolProcesso);
    montarLinha("Saída Etanol Hidratado (l)", saidaEtanolHidratado);
    montarLinha("Estoque Etanol Hidratado (l)", estoqueEtanolHidratado);
    montarLinha("Vinhaça Produzida (l)", vinhacaProduzida);
    montarLinha("Etanol Anidro Produzido (l)", etanolAnidroProduzido);
    montarLinha("INPM Etanol Anidro (inpm)", inpmEtanolAnidro);
    montarLinha("Saída Etanol Anidro (l)", saidaEtanolAnidro);
    montarLinha("Estoque Etanol Anidro (l)", estoqueEtanolAnidro);
    montarLinha("WDG Produzido (ton)", wdgProduzido);
    montarLinha("WDG / Milho Moído (kg/ton)", wdgMilhoMoido);
    montarLinha("Estoque WDG (ton)", estoqueWdg);
    montarLinha("DDG Produzido (ton)", ddgProduzido);
    montarLinha("DDG / Milho Moído (kg/ton)", ddgMilhoMoido);
    montarLinha("Estoque DDG (ton)", estoqueDdg);
    montarLinha("Etanol Total Hidratado (m³)", etanolTotalHidratado);
    montarLinha("KGS DDG p/ Lts Etanol Total (kg/l)", kgsDdgEtanolTotal);
    montarLinha("TON DDG p/ Etanol Hidratado (ton/ddg)", tonDdgEtanolHidratado);
    montarLinha("TON DDG p/ Etanol Anidro (ton/ddg)", tonDdgEtanolAnidro);
    montarLinha("KGS WDG p/ Lts Etanol Total (kg/l)", kgsEdgEtanolTotal);
    montarLinha("TON WDG p/ Etanol Hidratado (ton/DDG)", tonEdgEtanolHidratado);
    montarLinha("TON WDG p/ Etanol Anidro (ton/DDG)", tonEdgEtanolAnidro);
    montarLinha("Milho Moído p/ Etanol Hidratado (ton)", milhoMoidoEtanolHidratado);
    montarLinha("Milho Moído p/ Etanol Anidro (ton)", milhoMoidoEtanolAnidro);
    montarLinha("Milho Moído p/ Etanol Hidratado (%)", milhoMoidoEtanolHidratadoPorcentagem);
    montarLinha("Milho Moído p/ Anidro Hidratado (%)", milhoMoidoEtanolAnidroPorcentagem);
    montarLinha("Litros de Etanol / Milho Moído (l/ton)", litrosEtanolMilhoMoido);
    montarLinha("Litros de Etanol / Amido Base Seca (l/bs)", litrosEtanolAmidoBaseSeca);
    montarLinha("Rendimento Total (WDG+LDG+DDG) (kg)", rendimentoTotalWggLdgDdg);
    montarLinha("Amido Base Umida (%)", amidoBaseUmida);
    montarLinha("Umidade (%)", umidade);
    montarLinha("Amido Base Seca (%)", amidoBaseSeca);
    montarLinha("Impurezas (%)", impurezas);
    montarLinha("Quebrados (%)", quebrados);
    montarLinha("Avariados (%)", avariados);

    // Tabela com autoTable
    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 80,
      margin: { top: 50, left: 20, right: 20 },
      styles: {
        fontSize: 8,
        cellPadding: 4
      },
      headStyles: {
        fillColor: [7, 99, 51],
        textColor: 255
      },
      didDrawPage: () => {
        doc.setGState(new doc.GState({ opacity: 0.08 }));
        doc.addImage(marcaDaguaBase64, 'JPEG', centerX, centerY, marcaW, marcaH);
        doc.setGState(new doc.GState({ opacity: 1 }));
      }
    });

    doc.save(`boletim_milho_${new Date().toLocaleString("pt-BR")}.pdf`);
  };



  //===========================TELA CHEIA==============================//
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.log(`Error attempting to enable full - screen mode: ${err.message}(${err.name})`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };






  return (
    <>

      <Geral>
        <div>

          <Table>
            <Thead>
              <Tr noHover>
                <CabecalhoTabela colSpan={13}>
                  <HeaderWrapper>
                    <h2 >Milho</h2>
                    <HeaderRight>
                      <p >Atualizado: {horaRodou}</p>
                      <IconGroup>
                        <Button onClick={gerarPdfRelatorio} placeholder="Imprimir">
                          <FaDownload />
                        </Button>
                        <Button
                          onClick={zoomOut}
                          title="Diminuir zoom (Alt+-)"
                          aria-label="Diminuir zoom"
                          type="button"
                        >
                          <FiZoomOut />
                        </Button>
                        <Button
                          onClick={zoomIn}
                          title="Aumentar zoom (Alt+=)"
                          aria-label="Aumentar zoom"
                          type="button"
                        >
                          <FiZoomIn />
                        </Button>
                      </IconGroup>
                    </HeaderRight>
                  </HeaderWrapper>
                </CabecalhoTabela>

              </Tr>


              <Tr noHover>

                <Th width="22%" >Período:</Th>


                {Array.isArray(periodos) &&
                  periodos
                    .map((item, index) => ({
                      ...item,
                      codigo: codigosPeriodos[index] // adiciona código associado à posição
                    }))
                    .filter(p => !isMobile || periodosMobile.includes(p.codigo))
                    .map((item, index) => (
                      <Th key={index}>
                        <div className="tooltip">
                          {item.DATA}
                          <span className="tooltiptext">{item.PERIODO}</span>
                        </div>
                      </Th>
                    ))
                }


              </Tr>
            </Thead>

            <Tbody>

              <LinhaTabela titulo="Milho Recebido (ton)" dados2={milhoRecebido} />
              <LinhaTabela titulo="Milho Moído (ton)" dados2={milhoMoido} />
              <LinhaTabela titulo="Estoque Milho (ton)" dados2={estoqueMilho} />
              <LinhaTabela titulo="Dias de Safra (dia)" dados2={diasSafraMilho} />
              <LinhaTabela titulo="Horas de Safra (h)" dados2={horasSafraMilho} />
              <LinhaTabela titulo="Dias Efetivos de Moagem (dia)" dados2={diasEfetivosMoagem} />
              <LinhaTabela titulo="Horas Efetivas de Moagem (h)" dados2={horasEfetivasMoagem} />
              <LinhaTabela titulo="Horas Paradas Totais (h)" dados2={horasParadasTotais} />
              <LinhaTabela titulo="Horas Paradas Não Moinho (h)" dados2={horasParadasNaoMoinho} />
              <LinhaTabela titulo="Horas Paradas Moinho (h)" dados2={horasParadasMoinho} />
              <LinhaTabela titulo="Eficiência Industrial (%)" dados2={eficienciaIndustrial} />
              <LinhaTabela titulo="Eficiência Geral (%)" dados2={eficienciaGeral} />
              <LinhaTabela titulo="Moagem Horas Efetivas (%)" dados2={moagemHorasEfetivas} />
              <LinhaTabela titulo="Etanol Hidratado Produzido (l)" dados2={etanolHidratadoProduzido} />
              <LinhaTabela titulo="Etanol em Processo (l)" dados2={etanolProcesso} />
              <LinhaTabela titulo="INPM Etanol Hidratado (inpm)" dados2={inpmEtanolHidratado} />
              <LinhaTabela titulo="Saída Etanol Hidratado (l)" dados2={saidaEtanolHidratado} />
              <LinhaTabela titulo="Estoque Etanol Hidratado (l)" dados2={estoqueEtanolHidratado} />
              <LinhaTabela titulo="Vinhaça Produzida (l)" dados2={vinhacaProduzida} />
              <LinhaTabela titulo="Etanol Anidro Produzido (l)" dados2={etanolAnidroProduzido} />
              <LinhaTabela titulo="INPM Etanol Anidro (inpm)" dados2={inpmEtanolAnidro} />
              <LinhaTabela titulo="Saída Etanol Anidro (l)" dados2={saidaEtanolAnidro} />
              <LinhaTabela titulo="Estoque Etanol Anidro (l)" dados2={estoqueEtanolAnidro} />
              <LinhaTabela titulo="WDG Produzido (ton)" dados2={wdgProduzido} />
              <LinhaTabela titulo="WDG / Milho Moído(kg/ton)" dados2={wdgMilhoMoido} />
              <LinhaTabela titulo="Estoque WDG (ton)" dados2={estoqueWdg} />
              <LinhaTabela titulo="DDG Produzido (ton)" dados2={ddgProduzido} />
              <LinhaTabela titulo="DDG / Milho Moído (kg/ton)" dados2={ddgMilhoMoido} />
              <LinhaTabela titulo="Estoque DDG (ton)" dados2={estoqueDdg} />
              <LinhaTabela titulo="Etanol Total Hidratado (m³)" dados2={etanolTotalHidratado} />
              <LinhaTabela titulo="KGS DDG p/ Lts Etanol Total (kg/l)" dados2={kgsDdgEtanolTotal} />
              <LinhaTabela titulo="TON DDG p/ Etanol Hidratado (ton/ddg)" dados2={tonDdgEtanolHidratado} />
              <LinhaTabela titulo="TON DDG p/ Etanol Anidro (ton/ddg)" dados2={tonDdgEtanolAnidro} />
              <LinhaTabela titulo="KGS WDG p/ Lts Etanol Total (kg/l)" dados2={kgsEdgEtanolTotal} />
              <LinhaTabela titulo="TON WDG p/ Etanol Hidratado (ton/ddg)" dados2={tonEdgEtanolHidratado} />
              <LinhaTabela titulo="TON WDG p/ Etanol Anidro (ton/ddg)" dados2={tonEdgEtanolAnidro} />
              <LinhaTabela titulo="Milho Moído p/ Etanol Hidratado (ton)" dados2={milhoMoidoEtanolHidratado} />
              <LinhaTabela titulo="Milho Moído p/ Etanol Anidro (ton)" dados2={milhoMoidoEtanolAnidro} />
              <LinhaTabela titulo="Milho Moído p/ Etanol Hidratado (%)" dados2={milhoMoidoEtanolHidratadoPorcentagem} />
              <LinhaTabela titulo="Milho Moído p/ Anidro Hidratado (%)" dados2={milhoMoidoEtanolAnidroPorcentagem} />
              <LinhaTabela titulo="Litros de Etanol / Milho Moído (l/ton)" dados2={litrosEtanolMilhoMoido} />
              <LinhaTabela titulo="Litros de Etanol / Amido Base Seca (l/bs)" dados2={litrosEtanolAmidoBaseSeca} />
              <LinhaTabela titulo="Rendimento Total (WDG+LDG+DDG) (kg)" dados2={rendimentoTotalWggLdgDdg} />
              <LinhaTabela titulo="Amido Base Umida (%)" dados2={amidoBaseUmida} />
              <LinhaTabela titulo="Umidade (%)" dados2={umidade} />
              <LinhaTabela titulo="Amido Base Seca (%)" dados2={amidoBaseSeca} />
              <LinhaTabela titulo="Impurezas (%)" dados2={impurezas} />
              <LinhaTabela titulo="Quebrados (%)" dados2={quebrados} />
              <LinhaTabela titulo="Avariados (%)" dados2={avariados} />

            </Tbody>
          </Table>


        </div>

      </Geral >

    </>

  );
};



export default Grid;
